import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Container,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { buildReportsRegistry, type ReportDefinition } from '@/reports/registry';
import { useNotifications } from '@/stores/useNotificationStore';
import { getErrorMessage } from '@/utils/httpClient';
import { entityService } from '@/services/EntityService';
import { DYNAMIC_OPTIONS } from '@/reports/constants';
import { OUTPUT_FORMAT_KEY, type ReportOutputFormat } from '@/utils/reportOutput';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { useCatalogLocationLabels } from '@/hooks/useCatalogLocationLabels';

/** Shape of navigation state that can be passed to pre-select a report with form values */
export interface ReportsPageNavState {
  reportId?: string;
  formValues?: Record<string, unknown>;
}

export interface PersonOption {
  id: string;
  displayName: string;
  entityIdentifier: string | null;
}

type ReportFormValues = Record<string, unknown>;

/** Stable empty array to avoid re-render loops in controlled MUI Autocomplete multiple */
const EMPTY_PERSONS: PersonOption[] = [];

const ENABLED_REPORT_IDS = new Set<string>([
  'customer-survey',
  'allocation-summary',
  'transaction-completion',
  'asset-acquisition-order-form',
  'outstanding-pos',
  'cost-of-business',
  'inventory-trends',
  'inventory-awareness',
  'dx-report',
  'cleared-service-members',
  'suggest-on-hand-inventory',
  'change-document',
  'all-ie',
  'assets-currently-assigned',
  'assets-approaching-expiration',
  'expired-assets',
  'ie-items-not-on-sm-phr',
  'overdue-assets',
  'primary-restock',
]);

const resolveInitialValues = (report: ReportDefinition): ReportFormValues =>
  report.parameters.reduce<ReportFormValues>((acc, param) => {
    if (param.type !== 'person' && param.type !== 'multiperson' && param.defaultValue !== undefined) {
      acc[param.name] = param.defaultValue;
    }
    return acc;
  }, {});

const sortedReportsDefault = buildReportsRegistry();

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/[_\s-]+/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

export const ReportsPage: React.FC = () => {
  const location = useLocation();
  const navState = (location.state ?? null) as ReportsPageNavState | null;
  const person = usePersonLabels('reports');
  const { label: ieKitLabel, labelPlural: ieKitLabelPlural } = useIeKitLabels('reports');
  const catalogLocationLabels = useCatalogLocationLabels();

  const sortedReports = useMemo(
    () =>
      [
        ...buildReportsRegistry(
          person,
          ieKitLabel,
          ieKitLabelPlural,
          catalogLocationLabels.primaryLocationLabel,
          catalogLocationLabels.secondaryLocationLabel,
        ),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [
      person,
      ieKitLabel,
      ieKitLabelPlural,
      catalogLocationLabels.primaryLocationLabel,
      catalogLocationLabels.secondaryLocationLabel,
    ],
  );

  const [selectedReportId, setSelectedReportId] = useState<string | null>(
    navState?.reportId ?? sortedReportsDefault[0]?.id ?? null,
  );
  const [search, setSearch] = useState('');
  const [outputFormat, setOutputFormat] = useState<ReportOutputFormat>('html');
  const notifications = useNotifications();

  const selectedReport = useMemo<ReportDefinition | undefined>(
    () => sortedReports.find((report) => report.id === selectedReportId),
    [selectedReportId, sortedReports],
  );

  const filteredReports = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return sortedReports;
    }
    return sortedReports.filter(
      (report) =>
        multiWordMatch(`${report.name} ${report.description}`, query),
    );
  }, [search, sortedReports]);

  const [formValues, setFormValues] = useState<ReportFormValues>(() => {
    const defaults = selectedReport ? resolveInitialValues(selectedReport) : {};
    return navState?.formValues ? { ...defaults, ...navState.formValues } : defaults;
  });

  // Person picker state
  const [personSearchInputs, setPersonSearchInputs] = useState<Record<string, string>>({});
  const [personOptions, setPersonOptions] = useState<Record<string, PersonOption[]>>({});
  const [personLoading, setPersonLoading] = useState<Record<string, boolean>>({});

  // Dynamic multiselect options state
  const [multiselectOptions, setMultiselectOptions] = useState<Record<string, string[]>>({});
  const [multiselectLoading, setMultiselectLoading] = useState<Record<string, boolean>>({});
  const [multiselectErrors, setMultiselectErrors] = useState<Record<string, string | null>>({});

  // Person search effect - uses a ref to track active searches
  const personSearchTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const personSearchControllers = useRef<Record<string, AbortController>>({});

  const handlePersonSearch = useCallback((paramName: string, searchInput: string) => {
    // Clear previous timeout/controller for this param
    if (personSearchTimeouts.current[paramName]) {
      clearTimeout(personSearchTimeouts.current[paramName]);
    }
    if (personSearchControllers.current[paramName]) {
      personSearchControllers.current[paramName].abort();
    }

    setPersonSearchInputs((prev) => ({ ...prev, [paramName]: searchInput }));

    if (!searchInput.trim() || searchInput.length < 2) {
      setPersonOptions((prev) => ({ ...prev, [paramName]: [] }));
      setPersonLoading((prev) => ({ ...prev, [paramName]: false }));
      return;
    }

    setPersonLoading((prev) => ({ ...prev, [paramName]: true }));

    const controller = new AbortController();
    personSearchControllers.current[paramName] = controller;

    personSearchTimeouts.current[paramName] = setTimeout(async () => {
      try {
        const results = await entityService.searchEntities(searchInput, 'person');
        if (!controller.signal.aborted) {
          setPersonOptions((prev) => ({
            ...prev,
            [paramName]: results.map((e) => ({
              id: e.id,
              displayName: e.display_name ?? 'Unknown',
              entityIdentifier: e.entity_identifier ?? null,
            })),
          }));
        }
      } catch {
        // Ignore errors
      } finally {
        if (!controller.signal.aborted) {
          setPersonLoading((prev) => ({ ...prev, [paramName]: false }));
        }
      }
    }, 300);
  }, []);

  const handleSelectReport = (report: ReportDefinition) => {
    setSelectedReportId(report.id);
    setFormValues(resolveInitialValues(report));
  };

  // Scroll the pre-selected report into view when arriving via navigation state
  const selectedItemRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (navState?.reportId && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    // Only run on initial mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load dynamic multiselect options when report changes
  useEffect(() => {
    if (!selectedReport) return;

    const abortController = new AbortController();
    let mounted = true;

    const loadDynamicOptions = async () => {
      for (const param of selectedReport.parameters) {
        if (param.type === 'multiselect' && param.dynamicOptions === DYNAMIC_OPTIONS.IE_KITS) {
          setMultiselectLoading((prev) => ({ ...prev, [param.name]: true }));
          setMultiselectErrors((prev) => ({ ...prev, [param.name]: null }));

          try {
            // Fetch all IE kits using keyset pagination (AB#29164)
            const allIeKits = await entityService.listAll({
              type: 'ie_kit',
              order: 'display_name.asc',
              filters: { select: 'display_name,entity_data' },
            });

            if (!mounted || abortController.signal.aborted) return;

            // Deduplicate and filter out null/undefined names.
            // Fall back to entity_data.name if display_name is missing or
            // contains an unresolved template literal (e.g. '{{display_name}}').
            // Migration 138 (WI #28854) backfills display_name for existing entities;
            // this guard handles any residual rows not yet corrected by that migration.
            const resolveKitName = (kit: (typeof allIeKits)[number]): string | null => {
              const dn = kit.display_name;
              if (dn && dn !== '{{display_name}}') return dn;
              const name = (kit.entity_data as Record<string, unknown> | null)?.name;
              return typeof name === 'string' && name ? name : null;
            };
            const uniqueOptions = [
              ...new Set(
                allIeKits
                  .map(resolveKitName)
                  .filter((name): name is string => Boolean(name)),
              ),
            ];

            setMultiselectOptions((prev) => ({ ...prev, [param.name]: uniqueOptions }));
          } catch (error) {
            if (!mounted || abortController.signal.aborted) return;

            const errorMessage = getErrorMessage(error);
            console.error(`Failed to load ${ieKitLabel.toLowerCase()} options:`, error);
            notifications.error(`Failed to load ${ieKitLabel.toLowerCase()} options`, errorMessage);

            setMultiselectOptions((prev) => ({ ...prev, [param.name]: [] }));
            setMultiselectErrors((prev) => ({ ...prev, [param.name]: errorMessage }));
          } finally {
            if (mounted && !abortController.signal.aborted) {
              setMultiselectLoading((prev) => ({ ...prev, [param.name]: false }));
            }
          }
        }
      }
    };

    loadDynamicOptions();

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [selectedReport, notifications]);

  const handleChangeText = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeBoolean = (name: string, value: boolean) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeMultiSelect = (name: string, values: string[]) => {
    setFormValues((prev) => ({ ...prev, [name]: values }));
  };

  const [isRunning, setIsRunning] = useState(false);

  const selectedReportEnabled = Boolean(
    selectedReport && ENABLED_REPORT_IDS.has(selectedReport.id),
  );

  const handleRun = useCallback(async () => {
    if (!selectedReport || isRunning) {
      return;
    }

    const missingRequired = selectedReport.parameters.filter((param) => {
      if (!param.required) return false;
      if (param.type === 'boolean') return false;

      const raw =
        param.type === 'person' || param.type === 'multiperson'
          ? formValues[param.name]
          : (formValues[param.name] ?? param.defaultValue);
      if (param.type === 'multiselect') {
        return !Array.isArray(raw) || raw.length === 0;
      }
      if (param.type === 'person') {
        return raw == null || !(raw as PersonOption).id;
      }
      if (param.type === 'multiperson') {
        return !Array.isArray(raw) || (raw as PersonOption[]).length === 0;
      }
      if (raw == null) return true;
      if (typeof raw === 'string' && raw.trim().length === 0) return true;
      return false;
    });

    if (missingRequired.length > 0) {
      const labels = missingRequired.map((param) => param.label).join(', ');
      notifications.warning('Missing required parameters', `Please fill: ${labels}`);
      return;
    }

    setIsRunning(true);
    try {
      await selectedReport.run({ ...formValues, [OUTPUT_FORMAT_KEY]: outputFormat, personLabels: person, ieKitLabel, catalogLocationLabels });
    } catch (error) {
      const message = getErrorMessage(error) || 'Failed to generate report. Please try again.';
      notifications.error('Report generation failed', message);
    } finally {
      setIsRunning(false);
    }
  }, [selectedReport, formValues, outputFormat, notifications, isRunning]);

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Generate system reports. Choose a format below — HTML opens in a new tab, Excel downloads
        a spreadsheet, and PDF opens a print dialog.
      </Typography>

      <Card variant="outlined" sx={{ maxWidth: 1400, mx: 'auto', minHeight: '70vh' }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Available Reports
              </Typography>
              <Box sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search reports"
                  placeholder="Search by name or description..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </Box>
              <List
                dense
                sx={{
                  maxHeight: '60vh',
                  overflowY: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {filteredReports.map((report) => {
                  const isEnabled = ENABLED_REPORT_IDS.has(report.id);
                  return (
                    <ListItemButton
                      key={report.id}
                      ref={report.id === selectedReportId ? selectedItemRef : null}
                      selected={report.id === selectedReportId}
                      onClick={() => handleSelectReport(report)}
                    >
                      <ListItemText
                        primary={report.name}
                        secondary={report.description}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: isEnabled ? 'text.primary' : 'text.disabled',
                        }}
                        secondaryTypographyProps={{
                          color: isEnabled ? 'text.secondary' : 'text.disabled',
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>

            <Box sx={{ flex: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Parameters
              </Typography>
              {selectedReport ? (
                <>
                  {selectedReport.parameters.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      This report does not require any parameters.
                    </Typography>
                  )}

                  {selectedReport.parameters.map((param) => {
                    const value = formValues[param.name];

                    if (param.type === 'boolean') {
                      return (
                        <Box key={param.name} sx={{ mb: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(value ?? param.defaultValue)}
                                onChange={(event) =>
                                  handleChangeBoolean(param.name, event.target.checked)
                                }
                                disabled={!selectedReportEnabled}
                              />
                            }
                            label={param.label}
                          />
                        </Box>
                      );
                    }

                    if (param.type === 'select') {
                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            select
                            label={param.label}
                            value={(value as string) ?? param.defaultValue ?? ''}
                            onChange={(event) => handleChangeText(param.name, event.target.value)}
                            disabled={!selectedReportEnabled}
                          >
                            {!param.required && (
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                            )}
                            {param.options.map((option) => {
                              const val = typeof option === 'string' ? option : option.value;
                              const lbl = typeof option === 'string' ? toTitleCase(option) : option.label;
                              return (
                                <MenuItem key={val} value={val}>
                                  {lbl}
                                </MenuItem>
                              );
                            })}
                          </TextField>
                        </Box>
                      );
                    }

                    if (param.type === 'multiselect') {
                      const selectedValues = Array.isArray(value)
                        ? (value as string[])
                        : (param.defaultValue ?? []);

                      // Use dynamic options if available, otherwise use static options
                      const options = param.dynamicOptions
                        ? (multiselectOptions[param.name] ?? [])
                        : (param.options ?? []);

                      const loading = param.dynamicOptions
                        ? (multiselectLoading[param.name] ?? false)
                        : false;

                      const error = param.dynamicOptions
                        ? (multiselectErrors[param.name] ?? null)
                        : null;

                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <Autocomplete
                            multiple
                            options={options}
                            value={selectedValues}
                            onChange={(_, newValue) =>
                              handleChangeMultiSelect(param.name, newValue)
                            }
                            disableCloseOnSelect
                            disabled={!selectedReportEnabled}
                            loading={loading}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={param.label}
                                error={!!error}
                                helperText={error}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loading ? (
                                        <CircularProgress color="inherit" size={20} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>
                      );
                    }

                    if (param.type === 'person') {
                      const selectedPerson = value as PersonOption | null;
                      const options = personOptions[param.name] ?? [];
                      const loading = personLoading[param.name] ?? false;
                      const inputValue = personSearchInputs[param.name] ?? '';

                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <Autocomplete
                            options={options}
                            filterOptions={(x) => x}
                            getOptionLabel={(option) =>
                              `${option.displayName}${option.entityIdentifier ? ` (${option.entityIdentifier})` : ''}`
                            }
                            value={selectedPerson}
                            onChange={(_, newValue) => {
                              setFormValues((prev) => ({ ...prev, [param.name]: newValue }));
                            }}
                            inputValue={inputValue}
                            onInputChange={(_, newInputValue) =>
                              handlePersonSearch(param.name, newInputValue)
                            }
                            loading={loading}
                            isOptionEqualToValue={(option, val) => option.id === val.id}
                            disabled={!selectedReportEnabled}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={param.label}
                                placeholder="Start typing to search..."
                                slotProps={{
                                  input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {loading ? (
                                          <CircularProgress color="inherit" size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  },
                                }}
                              />
                            )}
                            noOptionsText={
                              inputValue.length < 2
                                ? 'Type at least 2 characters to search'
                                : `No ${person.lowerPlural} found`
                            }
                          />
                        </Box>
                      );
                    }

                    if (param.type === 'multiperson') {
                      const selectedPersons = (value as PersonOption[]) ?? EMPTY_PERSONS;
                      const options = personOptions[param.name] ?? [];
                      const loading = personLoading[param.name] ?? false;
                      const inputValue = personSearchInputs[param.name] ?? '';

                      return (
                        <Box key={param.name} sx={{ mb: 2 }}>
                          <Autocomplete
                            multiple
                            filterOptions={(x) => x}
                            options={options}
                            getOptionLabel={(option) =>
                              `${option.displayName}${option.entityIdentifier ? ` (${option.entityIdentifier})` : ''}`
                            }
                            value={selectedPersons}
                            onChange={(_, newValue) => {
                              setFormValues((prev) => ({ ...prev, [param.name]: newValue }));
                            }}
                            inputValue={inputValue}
                            onInputChange={(_, newInputValue) =>
                              handlePersonSearch(param.name, newInputValue)
                            }
                            loading={loading}
                            isOptionEqualToValue={(option, val) => option.id === val.id}
                            disabled={!selectedReportEnabled}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={param.label}
                                placeholder="Start typing to search..."
                                slotProps={{
                                  input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {loading ? (
                                          <CircularProgress color="inherit" size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  },
                                }}
                              />
                            )}
                            noOptionsText={
                              inputValue.length < 2
                                ? 'Type at least 2 characters to search'
                                : `No ${person.lowerPlural} found`
                            }
                          />
                        </Box>
                      );
                    }

                    return (
                      <Box key={param.name} sx={{ mb: 2 }}>
                        <TextField
                          fullWidth
                          label={param.label}
                          value={(value as string) ?? ''}
                          onChange={(event) => handleChangeText(param.name, event.target.value)}
                          type={param.type === 'date' ? 'date' : 'text'}
                          InputLabelProps={param.type === 'date' ? { shrink: true } : undefined}
                          disabled={!selectedReportEnabled}
                        />
                      </Box>
                    );
                  })}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Select a report from the list to configure parameters and run it.
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
          {selectedReport && !selectedReportEnabled && (
            <Typography variant="body2" color="error">
              Currently unavailable
            </Typography>
          )}
          <ToggleButtonGroup
            value={outputFormat}
            exclusive
            onChange={(_, value: ReportOutputFormat | null) => {
              if (value) setOutputFormat(value);
            }}
            size="small"
          >
            <ToggleButton value="html">HTML</ToggleButton>
            <ToggleButton value="excel">Excel</ToggleButton>
            <ToggleButton value="pdf">PDF</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            onClick={handleRun}
            disabled={!selectedReport || !selectedReportEnabled || isRunning}
            startIcon={isRunning ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {isRunning ? 'Generating…' : 'Run Report'}
          </Button>
        </CardActions>
      </Card>
    </Container>
  );
};
